// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TimezoneSearch } from '@/components/timezone-search';
import { MAXIMUM_DATE, MINIMUM_DATE } from '@/constants/date-shortcut';
import {
  AccessTime,
  KeyboardArrowDown,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Box, Popover, Stack, useTheme } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import { DateTime, Zone } from 'luxon';
import { useRef, useState } from 'react';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

interface PropsV1<Values extends FieldValues>
  extends UseControllerProps<Values> {
  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, past values are disabled and invalid.
   */
  disablePast?: boolean;

  /**
   * If true, future values are disabled and invalid.
   */
  disableFuture?: boolean;

  /**
   * The minimum selectable date/time. Dates before this will be disabled.
   */
  minimumDate?: DateTime;

  /**
   * The maximum selectable date/time. Dates after this will be disabled.
   */
  maximumDate?: DateTime;

  /**
   * If true, the input is disabled.
   */
  isDisabled?: boolean;

  /**
   * Persistent helper text rendered below the input when there is no field
   * error. The field error message takes precedence when set.
   */
  helperText?: string;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  isClearable?: boolean;
}

/**
 * A controlled date & time picker component that integrates with
 * react-hook-form.
 */
export function FormDateTimePicker<Values extends FieldValues>(
  props: PropsV1<Values>
) {
  // Disclosure hook for the timezone popover
  const timezonePopover = useDisclosure();
  // Ref for the timezone icon button
  const timezoneIconRef = useRef<HTMLDivElement>(null);
  // Theme object
  const theme = useTheme();
  // Controller for the datetime field
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // Get browser's IANA timezone (e.g., "America/Denver")
  const browserTimeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const browserTimeZone = DateTime.now().setZone(browserTimeZoneName).zone;

  // Value of the datetime field
  // Parse ISO strings and convert to browser timezone for display
  // This ensures times from the API are displayed in browser timezone regardless of the offset in the ISO string
  const value = (() => {
    if (typeof field.value === 'string') {
      return DateTime.fromISO(field.value).setZone(browserTimeZoneName);
    }
    return field.value ?? null;
  })();

  // Reference value for the timezone selector
  const referenceValue = value ?? undefined;
  // Timezone of the datetime field (or default to browser timezone)
  const timezone = value?.zone ?? browserTimeZone;
  // Whether to show the timezone label
  const showTimezoneLabel = value !== null;

  /**
   * Handles timezone changes from the timezone selector.
   * Updates the datetime with the new timezone and closes the popover.
   */
  const handleChangeTimezone = (zone: Zone) => {
    // If the datetime is null, do nothing
    if (value === null) {
      return;
    }

    // Update the datetime with the new timezone, preserving the indicated clock time
    const dateTime = value.setZone(zone, { keepLocalTime: true });

    // Update the field value with the DateTime object to preserve IANA zone info
    field.onChange(dateTime);

    // Close the timezone popover
    timezonePopover.close();
  };

  /**
   * Handles changes to the datetime value.
   * Preserves the hour when setting a new date if the current value is null.
   */
  const handleChange = (dateTime: DateTime | null) => {
    // If the datetime is null, set the field value to null
    if (dateTime === null) {
      return field.onChange(null);
    }

    // If the incoming dateTime doesn't have a proper IANA timezone, set it to the browser timezone
    // This ensures we always use IANA timezones like "America/Denver" instead of fixed offsets like "UTC"
    if (
      !dateTime.zoneName ||
      dateTime.zoneName === 'UTC' ||
      dateTime.zoneName.includes('UTC')
    ) {
      dateTime = dateTime.setZone(browserTimeZoneName);
    }

    // If the prior value was null, set the field value to the current date at 12:00 PM in the browser's timezone
    if (value === null) {
      // Set the datetime to 12:00 PM with 0 seconds and milliseconds
      dateTime = dateTime.set({
        hour: 12,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      // Set the timezone to the browser's timezone
      dateTime = dateTime.setZone(browserTimeZoneName, {
        keepLocalTime: true,
      });
    } else {
      // Always set seconds and milliseconds to 0 for existing values
      dateTime = dateTime.set({ second: 0, millisecond: 0 });
    }

    // Set the field value to the DateTime object to preserve IANA zone info
    field.onChange(dateTime);
  };

  /**
   * Toggles the timezone popover when the timezone icon is clicked.
   */
  const handleClickEditTimezone = () => {
    timezonePopover.isOpen ? timezonePopover.close() : timezonePopover.open();
  };

  return (
    <Stack flex={1}>
      <DateTimePicker
        format="yyyy-MM-dd hh:mm a"
        disabled={props.isDisabled}
        disableFuture={props.disableFuture}
        disablePast={props.disablePast}
        minDateTime={props.minimumDate ?? MINIMUM_DATE}
        maxDateTime={props.maximumDate ?? MAXIMUM_DATE}
        name={field.name}
        inputRef={field.ref}
        value={value}
        onChange={(value) => {
          handleChange(value);
        }}
        slotProps={{
          actionBar: {
            actions: props.isClearable ? ['clear'] : [],
          },
          textField: {
            size: 'small',
            label: props.label,
            error: Boolean(fieldState.error),
            fullWidth: true,
            helperText: fieldState.error?.message ?? props.helperText,
            autoComplete: 'off',
          },
        }}
      />
      {showTimezoneLabel && (
        <Box
          ref={timezoneIconRef}
          onClick={props.isDisabled ? undefined : handleClickEditTimezone}
          sx={{
            fontSize: '12px',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            ml: 1,
            mt: 0.25,
            cursor: props.isDisabled ? 'default' : 'pointer',
          }}
        >
          <AccessTime fontSize="inherit" sx={{ mr: 0.5 }} />{' '}
          {(() => {
            const parts = timezone.name.split(' ')[0].split('/');
            const displayName = parts.length > 1 ? parts[1] : parts[0];
            return displayName.replace(/_/g, ' ');
          })()}{' '}
          ({timezone.offsetName(value.toMillis(), { format: 'short' })})
          <KeyboardArrowDown fontSize="inherit" sx={{ ml: 0.5 }} />
        </Box>
      )}
      <Popover
        open={timezonePopover.isOpen && !props.isDisabled}
        anchorEl={timezoneIconRef.current}
        onClose={timezonePopover.close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        transitionDuration={theme.transitions.duration.standard}
      >
        <Stack width="30rem" padding={2} spacing={2}>
          <TimezoneSearch
            disableClearable
            label="Select time zone"
            value={timezone}
            reference={referenceValue}
            onChange={handleChangeTimezone}
          />
        </Stack>
      </Popover>
    </Stack>
  );
}

interface PropsV2<Values extends FieldValues>
  extends UseControllerProps<Values> {
  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, past values are disabled and invalid.
   */
  disablePast?: boolean;

  /**
   * If true, future values are disabled and invalid.
   */
  disableFuture?: boolean;

  /**
   * The minimum selectable date/time. Dates before this will be disabled.
   */
  minimumDate?: DateTime;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * Specifies whether the component or input field can be cleared by the user.
   * When set to true, it indicates that the user has the ability to remove the current value.
   * Typically used in selectable inputs or components with a clearable functionality.
   */
  isClearable?: boolean;

  /**
   * Callback function triggered when the value of the component changes.
   *
   * @param {DateTime | null} value - The new value of the component. It can
   * be a `DateTime` object representing the selected date and time or `null`
   * if the value is cleared or not set.
   */
  onChange?: (value: DateTime | null) => void;
}

/**
 * A controlled date & time picker component that integrates with
 * react-hook-form.
 *
 * @deprecated Use `FormDateTimePicker` instead. The functionality of this component is now included in `FormDateTimePicker`.
 */
export function FormDateTimePickerV2<Values extends FieldValues>(
  props: PropsV2<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // Get browser's IANA timezone (e.g., "America/Denver")
  const browserTimeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const browserTimeZone = DateTime.now().setZone(browserTimeZoneName).zone;

  // Parse the field value to a DateTime object
  // Parse ISO strings and convert to browser timezone for display
  // This ensures times from the API are displayed in browser timezone regardless of the offset in the ISO string
  const value = (() => {
    if (typeof field.value === 'string') {
      return DateTime.fromISO(field.value).setZone(browserTimeZoneName);
    }
    return field.value ?? null;
  })();

  // Get the timezone from the DateTime object's zone (or default to browser timezone)
  const selectedTimezone = (() => {
    if (!value?.isValid || !value.zone) {
      return browserTimeZone;
    }
    return value.zone;
  })();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleChangeTimezone = (zone: Zone) => {
    if (value === null) {
      return;
    }

    const dateTime = value.setZone(zone, { keepLocalTime: true });
    return field.onChange(dateTime);
  };

  const handleChange = (dateTime: DateTime | null) => {
    // If the datetime is null, set the field value to null
    if (dateTime === null) {
      return field.onChange(null);
    }

    // If the prior value was null, set the field value to the current date at 12:00 PM in the browser's timezone
    if (value === null) {
      // Extract the date components from the incoming dateTime (which may be in any timezone)
      const { year, month, day } = dateTime;
      // Create a new DateTime in the browser's timezone with hour set to 12 and 0 seconds/milliseconds
      const newDateTime = DateTime.fromObject(
        { year, month, day, hour: 12, minute: 0, second: 0, millisecond: 0 },
        { zone: browserTimeZoneName }
      );
      return field.onChange(newDateTime);
    }

    // If the incoming dateTime doesn't have a proper IANA timezone, set it to the browser timezone
    // This ensures we always use IANA timezones like "America/Denver" instead of fixed offsets like "UTC"
    if (
      !dateTime.zoneName ||
      dateTime.zoneName === 'UTC' ||
      dateTime.zoneName.includes('UTC')
    ) {
      dateTime = dateTime.setZone(browserTimeZoneName);
    }

    // Always set seconds and milliseconds to 0
    dateTime = dateTime.set({ second: 0, millisecond: 0 });

    // Set the field value to the DateTime object to preserve IANA zone info
    field.onChange(dateTime);
  };

  return (
    <Stack flex={1}>
      <DateTimePicker
        disabled={props.disabled}
        format="yyyy-MM-dd hh:mm a"
        disableFuture={props.disableFuture}
        disablePast={props.disablePast}
        minDateTime={props.minimumDate ?? MINIMUM_DATE}
        maxDateTime={MAXIMUM_DATE}
        name={field.name}
        inputRef={field.ref}
        value={value}
        onChange={(value) => {
          handleChange(value);
          if (props.onChange) {
            props.onChange(value);
          }
        }}
        slotProps={{
          actionBar: {
            actions: props.isClearable ? ['clear'] : [],
          },
          textField: {
            size: 'small',
            label: props.label,
            error: Boolean(fieldState.error),
            fullWidth: true,
            helperText: fieldState.error?.message,
            autoComplete: 'off',
          },
        }}
      />
      {value && (
        <Box
          onClick={(event) => {
            if (props.disabled) {
              return;
            }
            const parent = event.currentTarget;
            if (parent instanceof HTMLElement) {
              setAnchorEl(parent);
            } else {
              setAnchorEl(null);
            }
          }}
          sx={{
            fontSize: '12px',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            ml: 1,
            mt: 0.25,
            cursor: 'pointer',
          }}
        >
          <AccessTime fontSize="inherit" sx={{ mr: 0.5 }} />{' '}
          {(() => {
            const parts = selectedTimezone.name.split(' ')[0].split('/');
            const displayName = parts.length > 1 ? parts[1] : parts[0];
            return displayName.replace(/_/g, ' ');
          })()}{' '}
          ({selectedTimezone.offsetName(value.toMillis(), { format: 'short' })})
          <KeyboardArrowDown fontSize="inherit" sx={{ ml: 0.5 }} />
        </Box>
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        onClose={() => setAnchorEl(null)}
      >
        <Box sx={{ p: 2, width: '30rem' }}>
          <TimezoneSearch
            disableClearable
            label="Time zone"
            value={selectedTimezone}
            reference={value ?? undefined}
            onChange={handleChangeTimezone}
          />
        </Box>
      </Popover>
    </Stack>
  );
}
